"use client";

import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, deletePinCascade } from "@/core/offline/db";
import { deleteWithOfflineFallback } from "@/core/offline/delete-with-fallback";

/**
 * Hook pins au niveau site (modèle B, refactor sprint 8).
 * Les pins appartiennent à une zone physique (son plan) et à un site.
 * Ils persistent entre les campagnes — les mesures prises lors de chaque
 * campagne sont dans la table Mesure.
 *
 * @param zoneId Zone dont on veut les pins (tous, toutes campagnes confondues)
 */
export function usePins(zoneId: string) {
  const pins =
    useLiveQuery(
      () => offlineDb.pins.where("zoneId").equals(zoneId).toArray(),
      [zoneId]
    ) ?? [];

  /** Crée un pin sur la zone avec numérotation continue par site (transaction atomique) */
  async function createPin(x: number, y: number): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date();

    await offlineDb.transaction(
      "rw",
      [offlineDb.pins, offlineDb.zones],
      async () => {
        const zone = await offlineDb.zones.get(zoneId);
        if (!zone) throw new Error("Zone introuvable");

        const maxNumber = await getMaxPinNumberForSite(zone.siteId);

        await offlineDb.pins.add({
          id,
          number: maxNumber + 1,
          x,
          y,
          siteId: zone.siteId,
          zoneId,
          createdAt: now,
          updatedAt: now,
          syncStatus: "pending",
        });
      }
    );

    return id;
  }

  /** Déplace un pin à une nouvelle position */
  async function movePin(id: string, x: number, y: number) {
    await offlineDb.pins.update(id, {
      x,
      y,
      updatedAt: new Date(),
      syncStatus: "pending",
    });
  }

  /** Supprime un pin côté serveur puis localement (cascade).
   *  Offline : supprime localement, la sync propagera au serveur. */
  async function deletePin(id: string) {
    await deleteWithOfflineFallback("pin", id, `/api/pins/${id}`, () =>
      deletePinCascade(id)
    );
  }

  return { pins, createPin, movePin, deletePin };
}

// ─── Helpers numérotation site-wide ────────────────────────

/** Récupère tous les pins d'un site (cross-zones) — simplifié depuis modèle B */
async function getAllPinsForSite(siteId: string) {
  return offlineDb.pins.where("siteId").equals(siteId).toArray();
}

/** Retourne le numéro de pin le plus élevé sur tout le site (0 si aucun pin) */
async function getMaxPinNumberForSite(siteId: string): Promise<number> {
  const allPins = await getAllPinsForSite(siteId);
  return allPins.reduce((max, p) => Math.max(max, p.number), 0);
}

/** Renumérote tous les pins d'un site par ordre chronologique de création.
 *  Met aussi à jour le champ `numero` des mesures associées.
 *  Marque les entités modifiées comme "pending" pour la sync. */
export async function renumberSitePins(siteId: string): Promise<number> {
  return offlineDb.transaction(
    "rw",
    [offlineDb.pins, offlineDb.mesures],
    async () => {
      const allPins = await getAllPinsForSite(siteId);

      // Tri par date de création (chronologique)
      allPins.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      const now = new Date();
      let changed = 0;

      for (let i = 0; i < allPins.length; i++) {
        const newNumber = i + 1;
        const pin = allPins[i];
        if (pin.number === newNumber) continue;

        await offlineDb.pins.update(pin.id, {
          number: newNumber,
          updatedAt: now,
          syncStatus: "pending",
        });

        // Mettre à jour le numero des mesures associées (toutes campagnes)
        const mesures = await offlineDb.mesures
          .where("pinId")
          .equals(pin.id)
          .toArray();
        for (const m of mesures) {
          await offlineDb.mesures.update(m.id, {
            numero: newNumber,
            updatedAt: now,
            syncStatus: "pending",
          });
        }

        changed++;
      }

      return changed;
    }
  );
}
